import React, { useEffect, useMemo } from 'react';
import { PetType } from '@spike/model';
import Button from 'components/UI/V3/Button/Button';
import { PetTypesSelector } from 'components/UI/V3/PetTypesSelector/PetTypesSelector';
import { ChipSelector, Element } from 'components/Service/ChipSelector';
import { useAuth, useMasterData, useMarketplace } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import useOrganizationMarketplacesByServiceCategory from 'hooks/useOrganizationMarketplacesByServiceCategory';
import Service from 'model/Service';
import styles from './DaycarePetTypeStep.module.scss';

interface DaycarePetTypeStepProps {
    selectedPetTypes: Array<PetType>;
    marketplaces: Service['marketplaces'];
    serviceId?: number;
    onNext: (petTypes: Array<PetType>, marketplaces: Service['marketplaces']) => void;
}

export const DaycarePetTypeStep: React.FC<DaycarePetTypeStepProps> = props => {
    const masterData = useMasterData();
    const marketplace = useMarketplace();
    const auth = useAuth();
    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const daycareMarketplaces = useOrganizationMarketplacesByServiceCategory('daycare');

    const [selectedPetTypeIds, setSelectedPetTypeIds] = React.useState<Array<string>>(
        props.selectedPetTypes.map(pt => pt.id)
    );
    const [selectedMarketplaces, setSelectedMarketplaces] = React.useState<Service['marketplaces']>(props.marketplaces);

    // Filter pet types based on marketplace configuration
    const availablePetTypes = React.useMemo(() => {
        return masterData.petTypes.filter(petType => {
            if (petType.id === 'dogs' && marketplace.petTypes.dogs?.enabled) {
                return true;
            } else if (petType.id === 'cats' && marketplace.petTypes.cats?.enabled) {
                return true;
            } else if (petType.id === 'exotics' && marketplace.petTypes.exotics?.enabled) {
                return true;
            }
            return false;
        });
    }, [masterData.petTypes, marketplace.petTypes]);

    const loggedInMarketplace = useMemo(
        () => daycareMarketplaces.find(mp => mp.id === auth.marketplaceId),
        [daycareMarketplaces, auth.marketplaceId]
    );

    const marketplaceElements = useMemo<Array<Element<number>>>(
        () =>
            daycareMarketplaces
                .filter(mp => mp.active)
                .map(mp => ({
                    id: mp.id,
                    name: mp.name
                }))
                .sort((left, right) => left.name.toLocaleLowerCase().localeCompare(right.name.toLocaleLowerCase())),
        [daycareMarketplaces]
    );

    const selectedMarketplaceElements = useMemo<Array<Element<number>>>(
        () =>
            selectedMarketplaces
                .map(mp => {
                    const organizationMarketplace = organizationMarketplaces.find(om => om.id === mp.marketplaceId);

                    return organizationMarketplace
                        ? { id: organizationMarketplace.id, name: organizationMarketplace.name }
                        : null;
                })
                .filter((element): element is Element<number> => element !== null),
        [selectedMarketplaces, organizationMarketplaces]
    );

    useEffect(() => {
        // Only auto-select the logged-in marketplace when creating a service.
        if (props.serviceId !== undefined || selectedMarketplaces.length > 0 || !loggedInMarketplace) {
            return;
        }

        setSelectedMarketplaces([
            {
                marketplaceId: loggedInMarketplace.id,
                staffIds: [],
                taxes: [],
                roomIds: [],
                roomUnitIds: [],
                vehicleIds: []
            }
        ]);
    }, [loggedInMarketplace, props.serviceId, selectedMarketplaces.length]);

    const isValid = React.useMemo(() => {
        return selectedPetTypeIds.length > 0 && selectedMarketplaces.length > 0;
    }, [selectedPetTypeIds, selectedMarketplaces]);

    const handlePetTypesChange = (petTypeIds: Array<string>) => {
        setSelectedPetTypeIds(petTypeIds);
    };

    const changeMarketplacesSelectionHandler = (elements: Array<Element<number>>): void => {
        setSelectedMarketplaces(
            elements.map(element => {
                const existingMarketplace = selectedMarketplaces.find(
                    marketplace => marketplace.marketplaceId === element.id
                );

                return {
                    marketplaceId: element.id,
                    staffIds: existingMarketplace?.staffIds ?? [],
                    taxes: existingMarketplace?.taxes ?? [],
                    roomIds: existingMarketplace?.roomIds ?? [],
                    roomUnitIds: existingMarketplace?.roomUnitIds ?? [],
                    vehicleIds: existingMarketplace?.vehicleIds ?? []
                };
            })
        );
    };

    const handleNext = () => {
        if (isValid) {
            // Convert selected IDs back to PetType objects
            const selectedPetTypes = availablePetTypes.filter(pt => selectedPetTypeIds.includes(pt.id));
            props.onNext(selectedPetTypes, selectedMarketplaces);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.selectorContainer}>
                <PetTypesSelector
                    petTypes={availablePetTypes}
                    selectedPetTypeIds={selectedPetTypeIds}
                    onChange={handlePetTypesChange}
                    className={styles.petTypesSelector}
                />
            </div>

            <div className={styles.locationsContainer}>
                <p className={styles.locationsTitle}>Select locations</p>
                <ChipSelector
                    id="daycare_add_service_pet_type_locations"
                    searchLabel="Search locations"
                    allLabel="All Locations"
                    elements={marketplaceElements}
                    selectedElements={selectedMarketplaceElements}
                    changeSelection={changeMarketplacesSelectionHandler}
                />
            </div>

            <div className={styles.buttonContainer}>
                <Button
                    label="Next"
                    variant="primary"
                    size="large"
                    disabled={!isValid}
                    onClick={handleNext}
                />
            </div>
        </div>
    );
};
